import { create } from 'zustand';
import { archiveZone, createZone, getZoneCollectionPoints, listZones, updateZone } from '../services/zone.service';
import type {
  Zone,
  ZoneCollectionPointsResponse,
  ZoneInput,
  ZonePagination,
  ZoneQuery,
  ZoneUpdateInput,
} from '../types/zone.type';

interface ZoneState {
  zones: Zone[];
  total: number;
  page: number;
  limit: number;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  selectedZoneDetails: ZoneCollectionPointsResponse | null;
  selectedZoneLoading: boolean;
  clearError: () => void;
  fetchZones: (query: ZoneQuery) => Promise<ZonePagination | null>;
  fetchZoneCollectionPoints: (organizationId: string, zoneId: string) => Promise<ZoneCollectionPointsResponse | null>;
  createZone: (organizationId: string, input: ZoneInput) => Promise<Zone | null>;
  updateZone: (organizationId: string, zoneId: string, input: ZoneUpdateInput) => Promise<Zone | null>;
  archiveZone: (organizationId: string, zoneId: string) => Promise<boolean>;
}

function errorMessage(error: unknown, fallback: string): string {
  const candidate = error as { response?: { data?: { detail?: string | { message?: string } } } };
  const detail = candidate.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return fallback;
}

export const useZoneStore = create<ZoneState>((set) => ({
  zones: [],
  total: 0,
  page: 1,
  limit: 20,
  isLoading: false,
  isSaving: false,
  error: null,
  selectedZoneDetails: null,
  selectedZoneLoading: false,
  clearError: () => set({ error: null }),

  fetchZones: async (query) => {
    set({ isLoading: true, error: null });
    try {
      const result = await listZones(query);
      set({ zones: result.zones, total: result.total, page: result.page, limit: result.limit, isLoading: false });
      return result;
    } catch (error) {
      set({ isLoading: false, error: errorMessage(error, 'Failed to load zones') });
      return null;
    }
  },

  fetchZoneCollectionPoints: async (organizationId, zoneId) => {
    set({ selectedZoneLoading: true, error: null });
    try {
      const details = await getZoneCollectionPoints(organizationId, zoneId);
      set({ selectedZoneDetails: details, selectedZoneLoading: false });
      return details;
    } catch (error) {
      set({ selectedZoneLoading: false, error: errorMessage(error, 'Failed to load zone details') });
      return null;
    }
  },

  createZone: async (organizationId, input) => {
    set({ isSaving: true, error: null });
    try {
      const zone = await createZone(organizationId, input);
      set((state) => ({ zones: [zone, ...state.zones], total: state.total + 1, isSaving: false }));
      return zone;
    } catch (error) {
      set({ isSaving: false, error: errorMessage(error, 'Failed to create zone') });
      return null;
    }
  },

  updateZone: async (organizationId, zoneId, input) => {
    set({ isSaving: true, error: null });
    try {
      const zone = await updateZone(organizationId, zoneId, input);
      set((state) => ({ zones: state.zones.map((row) => row.id === zoneId ? zone : row), isSaving: false }));
      return zone;
    } catch (error) {
      set({ isSaving: false, error: errorMessage(error, 'Failed to update zone') });
      return null;
    }
  },

  archiveZone: async (organizationId, zoneId) => {
    set({ isSaving: true, error: null });
    try {
      await archiveZone(organizationId, zoneId);
      set((state) => ({
        zones: state.zones.filter((row) => row.id !== zoneId),
        total: Math.max(0, state.total - 1),
        isSaving: false,
      }));
      return true;
    } catch (error) {
      set({ isSaving: false, error: errorMessage(error, 'Failed to archive zone') });
      return false;
    }
  },
}));
